import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import { ISurveyBuilderSurvey } from "../../../@types/SurveyBuilder";
import { FixedTranslationsLanguageCodes } from "../../../@types/Translations";
import { IOption } from "../../../components/SelectDropdown";
import {
  surveyBuilderQueryKey,
  useSurveyBuilder,
  useSurveyBuilderOptions,
  useSurveyBuilderSave,
} from "../../../hooks/queries/surveyBuilder";
import { useToast } from "../../../hooks/useToast";
import {
  BuilderLayout,
  ISectionButton,
} from "../../../layout/components/BuilderLayout/BuilderLayout";
import { surveyBuilderService } from "../../../services/surveyBuilderService";
import { translationService } from "../../../services/translationsService";
import { getTranslatedSurveyOptions } from "../../../utils/surveyOptionLabels";
import { parseSurveyTranslationResponseToDisplay } from "../../../utils/translationsUtils";
import { useConfirmAction } from "./Components/ConfirmActionModal/ConfirmActionModal";
import { SurveyBuilderContentTab } from "./Components/ContentTab";
import { DisplayTabPanel } from "./Components/DisplayTab/DisplayTabPanel";
import {
  canCreateSurvey,
  emptyNewSurvey,
  INewSurveyValues,
  NewSurveyForm,
} from "./Components/NewSurveyForm/NewSurveyForm";
import { PreviewTab } from "./Components/PreviewTab/PreviewTab";
import { VersionBar } from "./Components/VersionBar/VersionBar";
import {
  BodyContainer,
  ScrollableContainer,
  TabPanel,
} from "./SurveyBuilder.styles";

export interface IDisplayRulesOptions {
  displayRulesTypes: IOption[];
  endComparators: IOption[];
  startComparators: IOption[];
}

type TabType = "content" | "preview" | "display";

/** Texts are written in this language unless the builder switches to another one. */
const DEFAULT_LANGUAGE = "en-GB";

/**
 * EN-5261: the versioned survey builder. A survey has published versions and at most one
 * draft; only the draft is editable, and every card saves on its own endpoint. Publishing
 * makes the draft the live version patients answer.
 */
export function SurveyBuilder() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { surveyId } = useParams();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [tab, setTab] = useState<TabType>("content");
  const [languageCode, setLanguageCode] = useState(DEFAULT_LANGUAGE);
  const [versionId, setVersionId] = useState<string>();
  const [issues, setIssues] = useState<string[]>([]);
  const [newSurvey, setNewSurvey] = useState<INewSurveyValues>(emptyNewSurvey);
  const [loadingTranslations, setLoadingTranslations] = useState(true);
  // The preview iframe loads a whole app: once opened, keep it mounted across tabs.
  const [previewOpened, setPreviewOpened] = useState(false);

  const { options, isOptionsLoading } = useSurveyBuilderOptions();
  const { survey, isLoading } = useSurveyBuilder(
    surveyId,
    versionId,
    languageCode
  );
  const { run, isSaving } = useSurveyBuilderSave(surveyId, languageCode);
  const confirm = useConfirmAction();

  // The first load picks the version (draft, else live); pin it so saves land in the same cache entry.
  useEffect(() => {
    if (survey && !versionId) {
      queryClient.setQueryData(
        surveyBuilderQueryKey(survey.id, survey.version.id, languageCode),
        survey
      );
      setVersionId(survey.version.id);
    }
  }, [survey, versionId]);

  // The labels of the fixed lists (survey types, strategies, graph types) are survey translations.
  useEffect(() => {
    translationService
      .getSurveysFixedTranslationsByLanguageCode(
        i18n.language as FixedTranslationsLanguageCodes
      )
      .then((translations) => {
        if (translations.data) {
          i18n.addResourceBundle(
            i18n.language,
            "translation",
            parseSurveyTranslationResponseToDisplay(translations.data)
          );
        }
      })
      .finally(() => setLoadingTranslations(false));
  }, []);

  const surveyTypeOptions = useMemo(
    () =>
      getTranslatedSurveyOptions(
        "surveyType",
        (options?.surveyTypes ?? []).map((type) => ({
          value: type,
          label: type,
          key: type,
        })),
        t
      ),
    [options, t]
  );

  const scoreStrategyOptions = useMemo(
    () =>
      getTranslatedSurveyOptions(
        "scoreStrategy",
        (options?.scoreStrategies ?? []).map((strategy) => ({
          value: strategy.type,
          label: strategy.name,
          key: strategy.type,
        })),
        t
      ),
    [options, t]
  );

  const displayRulesOptions = useMemo<IDisplayRulesOptions>(() => {
    const comparators = options?.comparators ?? [];
    const toOption = (comparator: string) => ({
      label: comparator,
      value: comparator,
      key: comparator,
    });

    return {
      displayRulesTypes: getTranslatedSurveyOptions(
        "displayRule",
        (options?.displayRuleTypes ?? []).map((rule) => ({
          label: rule.name,
          value: rule.type,
          key: rule.type,
        })),
        t
      ),
      endComparators: comparators
        .filter((c) => c.includes("<") && c !== "=")
        .map(toOption),
      startComparators: comparators
        .filter((c) => !c.includes("<"))
        .map(toOption),
    };
  }, [options, t]);

  const openVersion = (id: string) => {
    setIssues([]);
    setVersionId(id);
  };

  const versionAction = async (
    action: () => Promise<ISurveyBuilderSurvey>,
    message: string
  ) => {
    const result = await run(action, message);
    if (result) openVersion(result.version.id);
  };

  const handlePublish = async () => {
    if (!survey) return;
    const { version } = survey;

    try {
      const check = await surveyBuilderService.publishCheck(version.id);
      setIssues(check.issues);
      if (!check.canPublish) {
        showToast(t("surveys_builder_publish_blocked"), "error");
        return;
      }
    } catch (error) {
      showToast((error as Error).message, "error");
      return;
    }

    await versionAction(
      () => surveyBuilderService.publish(version.id, languageCode),
      t("surveys_builder_toasts_published", { number: version.number })
    );
  };

  const handleCreate = async () => {
    if (!canCreateSurvey(newSurvey)) return;

    const created = await run(() =>
      surveyBuilderService.createSurvey({
        type: newSurvey.type!,
        code: newSurvey.code || undefined,
        languageCode,
        name: newSurvey.name.trim(),
        description: newSurvey.description.trim() || undefined,
      })
    );
    if (created) {
      navigate(`/surveybuilder/${created.id}`, { replace: true });
    }
  };

  const tabButton = (name: TabType, label: string): ISectionButton => ({
    name,
    label,
    isSelected: tab === name,
    onButtonClick: () => {
      setTab(name);
      if (name === "preview") setPreviewOpened(true);
    },
  });

  const isLayoutLoading =
    isOptionsLoading || loadingTranslations || (!!surveyId && isLoading);

  if (!surveyId) {
    return (
      <BuilderLayout
        location="Surveys"
        isLayoutLoading={isLayoutLoading}
        handleOnCancel={() => navigate("/surveys")}
        handleOnSave={handleCreate}
        saveLabel={t("surveys_builder_new_create")}
        isSaveDisabled={!canCreateSurvey(newSurvey)}
        isWhiteButtonLoading={isSaving}
      >
        <ScrollableContainer noTabs>
          <BodyContainer>
            <NewSurveyForm
              values={newSurvey}
              surveyTypeOptions={surveyTypeOptions}
              onChange={setNewSurvey}
            />
          </BodyContainer>
        </ScrollableContainer>
      </BuilderLayout>
    );
  }

  const version = survey?.version;
  const isDraft = version?.status === "Draft";
  const isLive = !!survey && survey.liveVersionId === version?.id;
  const lastEdited = survey?.versions.find(
    (v) => v.id === version?.id
  )?.lastModified;

  const createDraft = () =>
    survey &&
    versionAction(
      () =>
        surveyBuilderService.createDraft(
          survey.id,
          survey.version.id,
          languageCode
        ),
      t("surveys_builder_toasts_draftCreated")
    );

  const makeLive =
    survey && version && !isDraft && !isLive
      ? () =>
          confirm({
            title: t("surveys_builder_confirm_makeLiveTitle", {
              number: version.number,
            }),
            body: t("surveys_builder_versions_makeLiveConfirm", {
              current: survey.versions.find((v) => v.isLive)?.number,
              number: version.number,
            }),
            confirmLabel: t("surveys_builder_versions_makeLive"),
            onConfirm: () =>
              versionAction(
                () =>
                  surveyBuilderService.makeLive(
                    survey.id,
                    version.id,
                    languageCode
                  ),
                t("surveys_builder_toasts_madeLive", { number: version.number })
              ),
          })
      : undefined;

  // The header's main button: publish the draft; make a previous version live; on the live version, open
  // or start the draft (drafts are only created from the live version).
  let mainLabel = t("builder_publish");
  let mainAction: () => void = handlePublish;
  if (makeLive) {
    mainLabel = t("surveys_builder_versions_makeLive");
    mainAction = makeLive;
  } else if (survey && !isDraft) {
    if (survey.draftVersionId) {
      const draftId = survey.draftVersionId;
      mainLabel = t("surveys_builder_versions_openDraft");
      mainAction = () => openVersion(draftId);
    } else {
      mainLabel = t("surveys_builder_versions_createDraft");
      mainAction = createDraft;
    }
  }

  const extraActions: { label: string; onClick: () => void }[] = [];
  if (survey && isDraft && survey.versions.length > 1) {
    extraActions.push({
      label: t("surveys_builder_versions_discardDraft"),
      onClick: () =>
        confirm({
          title: t("surveys_builder_confirm_discardTitle"),
          body: t("surveys_builder_versions_discardConfirm"),
          confirmLabel: t("surveys_builder_versions_discardDraft"),
          onConfirm: () =>
            versionAction(
              () => surveyBuilderService.discardDraft(survey.id, languageCode),
              t("surveys_builder_toasts_draftDiscarded")
            ),
        }),
    });
  }

  return (
    <BuilderLayout
      location="Surveys"
      isLayoutLoading={isLayoutLoading || !survey}
      lastEdited={lastEdited}
      handleOnCancel={() => navigate("/surveys")}
      handleOnSave={mainAction}
      saveLabel={mainLabel}
      extraActions={extraActions}
      isWhiteButtonLoading={isSaving}
      sectionButtons={[
        tabButton("content", t("surveys_builder_tabs_content")),
        tabButton("preview", t("surveys_builder_tabs_preview")),
        tabButton("display", t("surveys_builder_tabs_display")),
      ]}
    >
      {survey && (
        <ScrollableContainer>
          <BodyContainer>
            <VersionBar
              survey={survey}
              languageCode={languageCode}
              languages={options?.languages ?? []}
              issues={issues}
              onVersionChange={openVersion}
              onLanguageChange={setLanguageCode}
            />

            {tab === "content" && (
              <SurveyBuilderContentTab
                key={`${survey.version.id}-${languageCode}`}
                survey={survey}
                languageCode={languageCode}
                readOnly={!isDraft}
                surveyTypeLabel={
                  surveyTypeOptions.find((o) => o.value === survey.type)?.label
                }
                scoreStrategyOptions={scoreStrategyOptions}
                isSaving={isSaving}
                run={run}
              />
            )}
            {previewOpened && (
              <TabPanel hidden={tab !== "preview"}>
                <PreviewTab survey={survey} />
              </TabPanel>
            )}
            {tab === "display" && (
              <DisplayTabPanel
                key={`${survey.version.id}-${languageCode}`}
                survey={survey}
                languageCode={languageCode}
                readOnly={!isDraft}
                displayRulesOptions={displayRulesOptions}
                isSaving={isSaving}
                run={run}
              />
            )}
          </BodyContainer>
        </ScrollableContainer>
      )}
    </BuilderLayout>
  );
}
